import { useState } from "react"
import sl from "../../../../components/startups-landing/startups-landing-sections.module.css"
import textStyles from "../../../../shared/styles/section-text.module.css"
import styles from "./pricing-plans.module.css"
import { talentsSignupUrl } from "../../../../components/shared/constants/constants"

/** Baseline when paying the monthly rate for the full period (for savings calc). */
const MONTHLY_PRICE = 12

const BILLING_OPTIONS = [
	{
		id: "monthly",
		label: "Monthly",
		months: 1,
		billed: 12,
		priceDisplay: "$12",
		sub: "/ mo",
		note: "$12 billed monthly",
	},
	{
		id: "quarterly",
		label: "Quarterly",
		months: 3,
		billed: 27,
		priceDisplay: "$9",
		sub: "/ mo",
		note: "$27 billed every 3 months",
	},
	{
		id: "semi",
		label: "Semi-annual",
		months: 6,
		billed: 48,
		priceDisplay: "$8",
		sub: "/ mo",
		note: "$48 billed every 6 months",
	},
]

const FREE_FEATURES = [
	"1 professional profile",
	"1 active job search",
	"Resume export (PDF or shareable link)",
	"1 basic PDF template",
	"Job match notifications",
	"Email support",
]

const PREMIUM_FEATURES = [
	"All from Starter plan",
	"Up to 3 focused profiles",
	"Up to 3 simultaneous job searches",
	"Full PDF template library",
	"Primary email support",
]

function premiumSavingsLabel(billing) {
	if (billing.id === "monthly") return null
	const baseline = MONTHLY_PRICE * billing.months
	const save = baseline - billing.billed
	if (save <= 0) return null
	const pct = Math.round((save / baseline) * 100)
	return `Save ${pct}%`
}

const PricingPlans = () => {
	const [billingId, setBillingId] = useState("monthly")
	const billing = BILLING_OPTIONS.find((b) => b.id === billingId) ?? BILLING_OPTIONS[0]

	const saveBadge = premiumSavingsLabel(billing)

	return (
		<section id="pricing" className={styles.section}>
			<div className={`container ${sl.sectionContainer}`}>
				<header className={sl.pricingHeader}>
					<div className={sl.labelCenter}>Plans</div>
					<h2 className={styles.pricingTitle}>Simple pricing for people who value their time.</h2>
					<p className={`${sl.bodyLg} ${textStyles.centeredDescription}`}>
						Free to join, powerful when you need it. Create up to three focused profiles and run all three searches simultaneously, with Kate working across different roles, industries, or career directions at the same time.
					</p>
				</header>

				<div className={sl.pricingGrid}>
					<div className={sl.planCard}>
						<div className={sl.planName}>Starter</div>
						<div className={`${sl.planPrice} ${styles.planPriceFree}`}>Free</div>
						<div className={sl.planSub}>Best way to try Kate and keep your default resume in sync.</div>
						<ul className={sl.planFeatures}>
							{FREE_FEATURES.map((line) => (
								<li key={line}>
									<span className={sl.check}>✓</span> {line}
								</li>
							))}
						</ul>
						<a href={talentsSignupUrl} className={`${sl.btnGhost} ${sl.planBtn}`}>
							Get started
						</a>
					</div>

					<div className={`${sl.planCard} ${sl.planCardFeatured}`}>
						{saveBadge ? <span className={sl.planFeaturedBadge}>{saveBadge}</span> : null}
						<div className={sl.planName}>Premium</div>
						<div className={sl.planPrice}>
							{billing.priceDisplay} <span>{billing.sub}</span>
						</div>
						<div className={`${sl.planSub} ${styles.planSubPremium}`}>{billing.note}</div>
						<div className={styles.billingToolbar}>
							<p className={styles.billingToolbarLabel} id="billing-period-label">
								Billing period
							</p>
							<div
								className={styles.billingSwitch}
								role="group"
								aria-labelledby="billing-period-label"
							>
								{BILLING_OPTIONS.map((opt) => (
									<button
										key={opt.id}
										type="button"
										className={`${styles.billingBtn} ${opt.id === billingId ? styles.billingBtnActive : ""}`}
										onClick={() => setBillingId(opt.id)}
										aria-pressed={opt.id === billingId}
									>
										{opt.label}
									</button>
								))}
							</div>
						</div>
						<ul className={sl.planFeatures}>
							{PREMIUM_FEATURES.map((line) => (
								<li key={line}>
									<span className={sl.check}>✓</span> {line}
								</li>
							))}
						</ul>
						<a href={talentsSignupUrl} className={`${sl.btnPrimary} ${sl.planBtn}`}>
							Go Premium →
						</a>
					</div>
				</div>

				<p className={sl.pricingFootnote}>
					Prices shown are illustrative. Taxes may apply. You can change or cancel anytime in the app.
				</p>
			</div>
		</section>
	)
}

export default PricingPlans
